<template>
  <div class="m-6">
    <div class="md:flex md:items-center md:justify-between md:space-x-5">
      <div class="flex w-full items-center space-x-5">
        <div class="shrink-0">
          <div class="relative rounded-xl bg-purple-600/70 p-1">
            <mdicon name="text-search" size="62" class="text-purple-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="w-full">
          <div class="w-full">
            <div class="relative">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon class="size-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                id="search"
                v-model="searchQuery"
                name="search"
                class="sm:text-md block w-full rounded-md border-0 bg-white py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-purple-500/50 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:leading-6"
                placeholder='Enter keywords or "quoted phrase" to Search Conversations'
                type="search"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
        <ProgressButton ref="searchProgressButton" title="Search" icon="text-search" @click="loadData"></ProgressButton>
        <!--<Button icon="pi pi-upload" label="Upload" severity="secondary" @click="onUploadRecording" />-->
        <DropdownSelectButton icon="upload-box-outline" class="" title="Import" :items="importItems" @select="onImportSelect" />
        <button
          v-tooltip.bottom="'Data Assistant'"
          @click="onToggleDA"
          :class="[
            'px-2 mr-2 rounded border border-transparent shadow-gradient-glow-green',
            dataAssistantStore.visible ? 'text-slate-100 bg-emerald-600/70 hover:bg-emerald-500/70' : 'text-slate-600 hover:bg-slate-100/70',
          ]"
        >
          <mdicon name="creation" size="22" aria-hidden="true"></mdicon>
        </button>
      </div>
    </div>

    <FiltersList class="mt-2" @select-filter="onSelectFilter" @show-filters="showFilters"></FiltersList>

    <div class="overflow-hidden mt-2">
      <div class="flex items-center justify-between border-b border-gray-300 pb-1">
        <div class="flex items-center">
          <mdicon name="text" size="18" class="rounded-md bg-emerald-600/70 p-1 mr-2 text-white" aria-hidden="true"></mdicon>
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            <span class="text-lg text-slate-800 font-semibold">{{ conversationSearchStore.conversationsTotal }}</span> conversations over
            <span class="text-md text-slate-700">{{ analyzedDateRange }} (UTC)</span>
          </h2>
        </div>
        <div class="flex items-center"></div>
      </div>
      <DbUplot
        class="mt-2 h-[160px] w-full rounded-xl bg-gray-100/50"
        :data="uPlotData"
        :options="uPlotOptions"
        :tooltip="true"
        :title="`${conversationSearchStore.conversationsTotal} Conversations`"
        @db-event="handleDbEvent"
      ></DbUplot>
    </div>

    <div class="mt-2">
      <DataTable
        v-model:expanded-rows="expandedRows"
        v-model:filters="tableFiltersSpec"
        :value="conversations"
        lazy
        paginator
        :rows="25"
        :first="first"
        data-key="id"
        :total-records="totalRecords"
        :loading="loading"
        sort-field="datetime"
        :sort-order="-1"
        size="small"
        scrollable
        table-style="min-width: 50rem"
        filter-display="menu"
        row-hover
        @page="onPage($event)"
        @sort="onSort($event)"
      >
        <Column expander style="width: 1rem" />
        <Column header="Conversation" style="width: 50%" filter-field="tags" :show-filter-menu="true" :show-filter-match-modes="false" :show-clear-button="false" :show-apply-button="false">
          <template #filter="">
            <EditFilter :filter="filtersStore.getFilter('tags')" class=""></EditFilter>
          </template>
          <template #filterfooter="">
            <div class="flex justify-end">
              <div class="flex-0"><Button label="Edit Filters" icon="pi pi-filter" outlined size="small" @click="showFilters"></Button></div>
            </div>
          </template>
          <template #body="slotProps">
            <router-link :to="{ path: `/csat/conversation/${slotProps.data.id}` }">
              <div class="flex items-center">
                <div class="cursor-pointer font-source font-semibold hover:underline">
                  <!--<mdicon name="open-in-new" size="18" class="mt-1 text-slate-300"></mdicon>-->
                  {{ getConversationTitle(slotProps.data) }} ...
                </div>
              </div>
            </router-link>
            <div v-if="Array.isArray(slotProps.data.tags) && slotProps.data.tags.length > 0" class="mt-1 flex flex-wrap items-center">
              <div
                v-for="(tag, idx) in slotProps.data.tags"
                :key="idx"
                class="mr-1 flex cursor-pointer items-center rounded-md bg-slate-50 px-2 py-1 font-source text-sm font-medium text-sky-700 ring-1 ring-inset ring-blue-700/10 hover:underline"
                @click="onSetFilter('tags', [tag])"
              >
                <!--<router-link :to="{ path: route.path, query: { query: getSearchQueryTagConversations(tag) } }" class="flex items-center hover:underline">
                    <p>{{ tag }}</p>
                    <mdicon name="filter" size="16" class="text-slate-300"></mdicon>
                  </router-link>-->
                {{ tag }}
                <mdicon name="filter" size="20" class="text-slate-300"></mdicon>
              </div>
            </div>
          </template>
        </Column>
        <!-- TODO Instead of number, show media (chat, email, voice?), or source (ticket, recording)-->
        <!--
        <Column field="Number" header="Number" sortable :show-filter-menu="true" :show-filter-match-modes="false" :show-clear-button="false" :show-apply-button="false">
          <template #filter="{ filterModel }">
            <EditFilter :filter="filtersStore.getFilter('Number')" class="m-2 p-2"></EditFilter>
          </template>
          <template #filterfooter="">
            <div class="flex justify-end">
              <div class="flex-0"><Button label="Edit Filters" icon="pi pi-filter" outlined size="small" @click="showFilters"></Button></div>
            </div>
          </template>
          <template #body="slotProps">
            <div class="flex cursor-pointer items-center hover:underline" @click="onSetFilter('Number', [slotProps.data?.Number || null])">
              <div class="font-medium">{{ slotProps.data?.Number || '-' }}</div>
              <mdicon name="filter" size="20" class="ml-2 text-gray-300"></mdicon>
            </div>
            <div v-if="slotProps.data?.NumberName && slotProps.data.NumberName !== ''" class="text-xs text-gray-500">{{ slotProps.data?.NumberName }}</div>
          </template>
        </Column>
        -->
        <Column field="AgentName" header="Agent" sortable :show-filter-menu="true" :show-filter-match-modes="false" :show-clear-button="false" :show-apply-button="false">
          <template #filter="">
            <EditFilter :filter="filtersStore.getFilter('AgentName')" class="m-2 p-2"></EditFilter>
          </template>
          <template #filterfooter="">
            <div class="flex justify-end">
              <div class="flex-0"><Button label="Edit Filters" icon="pi pi-filter" outlined size="small" @click="showFilters"></Button></div>
            </div>
          </template>
          <template #body="slotProps">
            <div class="flex cursor-pointer items-center hover:underline" @click="onSetFilter('AgentName', [slotProps.data?.AgentName || null])">
              <div class="cursor-pointer font-medium">{{ slotProps.data?.AgentName || slotProps.data?.AgentId || '-' }}</div>
              <mdicon name="filter" size="20" class="ml-2 text-gray-300"></mdicon>
            </div>
          </template>
        </Column>
        <Column field="CSat" header="CSat" sortable :show-filter-menu="true" :show-filter-match-modes="false" :show-clear-button="false" :show-apply-button="false">
          <!--<template #filter="">
            <EditFilter :filter="filtersStore.getFilter('CSat')" class=""></EditFilter>
          </template>
          <template #filterfooter="">
            <div class="flex justify-end">
              <div class="flex-0"><Button label="Edit Filters" icon="pi pi-filter" @click="showFilters" outlined size="small"></Button></div>
            </div>
          </template>-->
          <template #body="slotProps">
            <span :class="['text-md rounded-sm px-2 py-1.5 font-source', getCSatClass(slotProps.data)]">
              {{ 'CSat' in slotProps.data ? (slotProps.data.CSat !== null ? slotProps.data.CSat : '-') : '-' }}
              <!--
              / {{ slotProps.data.context.analysis.kpis.find((x) => x.title === 'NPS').score }}
              -->
            </span>
          </template>
        </Column>
        <!--<Column field="KPIScore" header="KPI Score" sortable>
          <template #body="slotProps">
            <span :class="['px-2 py-1.5 font-source text-md rounded-md', getKPIScoreClass(slotProps.data)]">
              {{ slotProps.data?.KPIScore }}
            </span>
          </template>
        </Column>-->
        <Column field="duration" header="Duration" sortable>
          <template #body="slotProps">
            {{ timeUtils.formatDuration(slotProps.data?.duration || 0) }}
          </template>
        </Column>
        <Column field="datetime" header="Date" sortable>
          <template #body="slotProps">
            <div class="">{{ dayjs(slotProps.data?.datetime).format('MM/DD/YY HH:mm:ss') }}</div>
          </template>
        </Column>
        <template #expansion="slotProps">
          <div class="text-md prose font-source rounded-md ml-7 px-4 py-1 text-slate-600" v-html="getSummary(slotProps.data)"></div>
        </template>
      </DataTable>
    </div>
    <EditFiltersSidebar v-model:visible="filtersOpen"></EditFiltersSidebar>
    <CSatDialogUploadRecording ref="dialogUploadRecording" @recording-analyzed="onRecordingAnalyzed"></CSatDialogUploadRecording>
    <CSatDialogUploadZendeskTicket ref="dialogUploadZendeskTicket" @zendesk-ticket-analyzed="onZendeskTicketAnalyzed"></CSatDialogUploadZendeskTicket>
    <CSatDialogUploadFreshchatConversation ref="dialogUploadFreshchatConversation" @freshchat-conversation-analyzed="onFreshchatConversationAnalyzed"></CSatDialogUploadFreshchatConversation>
    <CSatDialogUploadFreshdeskTicket ref="dialogUploadFreshdeskTicket" @freshdesk-ticket-analyzed="onFreshdeskTicketAnalyzed"></CSatDialogUploadFreshdeskTicket>
    <CSatDialogUploadFreshserviceTicket ref="dialogUploadFreshserviceTicket" @freshservice-ticket-analyzed="onFreshserviceTicketAnalyzed"></CSatDialogUploadFreshserviceTicket>
    <CSatDialogUploadKustomerConversation ref="dialogUploadKustomerConversation" @kustomer-conversation-analyzed="onKustomerConversationAnalyzed"></CSatDialogUploadKustomerConversation>
    <CSatDialogUploadGorgiasTicket ref="dialogUploadGorgiasTicket" @kustomer-conversation-analyzed="onGorgiasTicketAnalyzed"></CSatDialogUploadGorgiasTicket>
    <CSatDialogUploadSalesForceCase ref="dialogUploadSalesForceCase" @saleforce-case-analyzed="onSalesForceCaseAnalyzed"></CSatDialogUploadSalesForceCase>
    <CSatDialogUploadIntercomConversation ref="dialogUploadIntercomConversation" @intercom-conversation-analyzed="onIntercomConversationAnalyzed"></CSatDialogUploadIntercomConversation>
    <CSatDialogDADataSelector ref="dialogDADataSelector" @data-selected="onDADataSelected"></CSatDialogDADataSelector>
  </div>
</template>
<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { ArrowDownCircleIcon } from '@heroicons/vue/20/solid';
  import { useConversationSearchStore } from '@/store/ConversationSearchStore';
  import { useDateRangeStore } from '@/store/DaterangeStore';
  import { useDataAssistantStore } from '@/store/DataAssistantStore';
  import { useFiltersStore } from '@/store/FiltersStore';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { useConversationHelpers } from '@/hooks/useConversationHelpers';
  import { useTimeUtils } from '@/hooks/useTimeUtils';
  import { stdConversationFilters } from '@/analytics/filtersDefs';
  import { useMd } from '@/hooks/useMd';
  import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid';
  import DataTable from 'primevue/datatable';
  import Column from 'primevue/column';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import DbDygraphsBar from '@/components/Charts/DbDygraphsBar.vue';
  import ProgressButton from '@/components/Buttons/ProgressButton.vue';
  import EditFiltersSidebar from '@/components/Filters/EditFiltersSidebar.vue';
  import FiltersList from '@/components/Filters/FiltersList.vue';
  import EditFilter from '@/components/Filters/EditFilter.vue';
  import Button from 'primevue/button';
  import DbUplot from '@/components/Charts/DbUplot.vue';
  import uPlot from 'uplot';
  import DropdownSelectButton from '@/components/Buttons/DropdownSelectButton.vue';
  import CSatDialogUploadRecording from '@/components/CSat/dialogs/CSatDialogUploadRecording.vue';
  import CSatDialogUploadZendeskTicket from '@/components/CSat/dialogs/CSatDialogUploadZendeskTicket.vue';
  import CSatDialogUploadFreshchatConversation from '@/components/CSat/dialogs/CSatDialogUploadFreshchatConversation.vue';
  import CSatDialogUploadFreshdeskTicket from '@/components/CSat/dialogs/CSatDialogUploadFreshdeskTicket.vue';
  import CSatDialogUploadFreshserviceTicket from '@/components/CSat/dialogs/CSatDialogUploadFreshserviceTicket.vue';
  import CSatDialogUploadKustomerConversation from '@/components/CSat/dialogs/CSatDialogUploadKustomerConversation.vue';
  import CSatDialogUploadGorgiasTicket from '@/components/CSat/dialogs/CSatDialogUploadGorgiasTicket.vue';
  import CSatDialogUploadSalesForceCase from '@/components/CSat/dialogs/CSatDialogUploadSalesForceCase.vue';
  import CSatDialogUploadIntercomConversation from '@/components/CSat/dialogs/CSatDialogUploadIntercomConversation.vue';
  import CSatDialogDADataSelector from '@/components/CSat/dialogs/CSatDialogDADataSelector.vue';

  dayjs.extend(utc);

  const route = useRoute();
  const conversationSearchStore = useConversationSearchStore();
  const dataAssistantStore = useDataAssistantStore();
  const dateRangeStore = useDateRangeStore();
  const filtersStore = useFiltersStore();
  const md = useMd();
  const settingsStore = useSettingsStore();
  const conversationHelpers = useConversationHelpers();
  const timeUtils = useTimeUtils();

  const q = decodeURIComponent(route?.query?.query || '');
  const queryFilter = route?.query?.filter || null;
  const searchQuery = ref(q);

  const searchProgressButton = ref(null);
  const dialogUploadRecording = ref(null);
  const dialogUploadZendeskTicket = ref(null);
  const dialogUploadFreshdeskTicket = ref(null);
  const dialogUploadFreshserviceTicket = ref(null);
  const dialogUploadFreshchatConversation = ref(null);
  const dialogUploadKustomerConversation = ref(null);
  const dialogUploadGorgiasTicket = ref(null);
  const dialogUploadSalesForceCase = ref(null);
  const dialogUploadIntercomConversation = ref(null);
  const dialogDADataSelector = ref(null);

  const filtersOpen = ref(false);
  const tableFiltersSpec = ref({
    tags: { value: null },
    Number: { value: null },
    AgentName: { value: null },
    CSat: { value: null },
  });

  // Search Results Table
  //const dt = ref();
  const loading = ref(false);
  const totalRecords = computed(() => conversationSearchStore.conversationsTotal);
  const first = computed(() => conversationSearchStore.searchOffset);
  const conversations = computed(() => conversationSearchStore.conversations);

  const expandedRows = ref([]);

  const analyzedDateRange = ref('');

  const dataTitle = 'Conversations';
  const suggestedQuestions = ['What insights can you derive from this data?', 'What are the most frequent topics of the conversations?'];

  const chartData = computed(() => conversationSearchStore.timeline);
  const chartOptions = {
    stackedGraph: true,
    //title: 'Conversations',
    ylabel: 'Conversations',
    labels: ['Date', 'Count'],
    //legend: 'follow',
  };

  const uPlotData = computed(() => conversationSearchStore.uPlotTimeline);
  const uPlotOptions = computed(() => {
    return {
      //height: 200,
      legend: {
        show: false,
      },
      tzDate: (ts) => uPlot.tzDate(new Date(ts * 1e3), 'Etc/UTC'),
      scales: {
        x: { time: true },
      },
      series: [
        {},
        {
          label: 'Conversations',
          paths: uPlot.paths.bars({ align: 0 }),
          stroke: 'rgba(78, 121, 167, 0.1 )',
          fill: 'rgba(78, 121, 167, 0.4)',
        },
      ],
    };
  });
  let importItems = ref([]);

  const setImportItems = async () => {
    await settingsStore.getElasticIndexSpan();
    importItems.value = [
      {
        type: 'recording',
        title: 'Import Recording',
        description: 'You can upload mp3 or wav file with call recording',
        icon: 'account-voice',
        enabled: true,
      },
      {
        type: 'zendeskTicket',
        title: 'Import Zendesk Ticket',
        description: 'You can import conversation from Zendesk ticket',
        icon: 'forum-outline',
        enabled: settingsStore.zendeskIntegrationEnabled,
      },
      {
        type: 'freshdeskTicket',
        title: 'Import Freshdesk Ticket',
        description: 'You can import conversation from Freshdesk ticket',
        icon: 'forum-outline',
        enabled: settingsStore.freshdeskIntegrationEnabled,
      },
      {
        type: 'freshserviceTicket',
        title: 'Import Freshservice Ticket',
        description: 'You can import conversation from Freshservice ticket',
        icon: 'forum-outline',
        enabled: settingsStore.freshserviceIntegrationEnabled,
      },
      {
        type: 'freshchatConversation',
        title: 'Import Freshchat Conversation',
        description: 'You can import conversation from Freshchat',
        icon: 'forum-outline',
        enabled: settingsStore.freshchatIntegrationEnabled,
      },
      {
        type: 'kustomerConversation',
        title: 'Import Kustomer Conversation',
        description: 'You can import conversation from Kustomer',
        icon: 'forum-outline',
        enabled: settingsStore.kustomerIntegrationEnabled,
      },
      {
        type: 'gorgiasTicket',
        title: 'Import Gorgias Ticket',
        description: 'You can import ticket from Gorgias',
        icon: 'forum-outline',
        enabled: settingsStore.gorgiasIntegrationEnabled,
      },
      {
        type: 'salesForceCase',
        title: 'Import Salesforce Case',
        description: 'You can import Case from Salesforce',
        icon: 'forum-outline',
        enabled: settingsStore.salesforceIntegrationEnabled,
      },
      {
        type: 'intercomConversation',
        title: 'Import Intercom Conversation',
        description: 'You can import Conversation by ID from Intercom',
        icon: 'forum-outline',
        enabled: settingsStore.intercomIntegrationEnabled,
      },
    ].filter((item) => item.enabled);
  };

  watch(
    route,
    (newRoute) => {
      const nq = decodeURIComponent(newRoute?.query?.query || '');
      if (nq !== searchQuery.value) {
        console.log(`Route query changed: ${nq}`);
        searchQuery.value = nq;
        searchProgressButton.value.startProgress();
        loadData();
      }
    },
    { immediate: true },
  );

  watch(filtersStore.allFilters, (newValue) => {
    if (loading.value) {
      console.log(`Already loading - ignore filters change`);
      return;
    }
    console.log(`Filters changed: ${JSON.stringify(newValue)}`);
    loadData();
  });

  watch(
    () => dateRangeStore.timeIntervalChanged,
    (newValue) => {
      console.log(`Date range changed: ${JSON.stringify(newValue)}`);
      loadData();
    },
  );

  const onPage = async (event) => {
    console.log(`onPage: ${JSON.stringify(event)}`);
    if ('first' in event && 'rows' in event) {
      loading.value = true;
      await conversationSearchStore.page(event.first, event.rows);
      loading.value = false;
    }
  };

  const onSort = async (event) => {
    console.log(`onSort: ${JSON.stringify(event)}`);
    // [{field:'name',type:'asc|desc'},...]
    if ('sortField' in event && 'sortOrder' in event) {
      const sortFields = [
        {
          field: event.sortField,
          type: event.sortOrder === 1 ? 'asc' : 'desc',
        },
      ];
      loading.value = true;
      await conversationSearchStore.sort(sortFields);
      loading.value = false;
    }
  };

  async function loadData() {
    loading.value = true;
    await conversationSearchStore.search(searchQuery.value, 25, 0, [{ field: 'datetime', type: 'desc' }]);
    loading.value = false;
    searchProgressButton.value.stopProgress();
    const startDate = dateRangeStore.fromTs;
    const endDate = dateRangeStore.toTs;
    analyzedDateRange.value = `${dayjs.utc(startDate).format('YYYY-MM-DD')} - ${dayjs.utc(endDate).format('YYYY-MM-DD')}`;
  }

  /*
  const rowClass = (data) => {
    //return [{ 'bg-slate-400': true }];
    return ['rounded-xl'];
  };
  const rowStyle = (data) => {
    return {}; //{ 'background-color': '#f8fafc' };
  };
  */

  function getCSatClass(data) {
    if (!('CSat' in data) || data.CSat === null) {
      return '';
    }
    let s = data.CSat;
    return s < 5 ? `bg-red-400/10` : `bg-green-400/30`;
  }

  // Get a title for the conversation, as a first customer message
  function getConversationTitle(conv) {
    const messages = conv?.context?.transcript?.data || [];
    if (!Array.isArray(messages) || messages.length <= 0) {
      return conv.id;
    }
    const customerMessage = messages.find((x) => x.actor === 'customer') || messages[0];
    if (!customerMessage || !customerMessage?.message || customerMessage.message === '') {
      return messages[0]?.message || conv.id;
    }
    return customerMessage.message.substring(0, 100);
  }

  function getCSatExplanation(conv) {
    return md.mdToHtml(conv?.CSatExplanation || '');
  }

  function getSummary(conv) {
    const summary = conv?.context?.analysis?.summary || 'Summary is not available';
    return md.mdToHtml(summary);
  }

  function handleDbEvent(event) {
    switch (event.type) {
      case 'zoom': {
        console.log(`Handle Zoom`, event);
        let from = event?.minDate || null;
        let to = event?.maxDate || null;
        if (from && to) {
          /* TODO Remove
          event.g.updateOptions({
            dateWindow: null,
            valueRange: null,
          }); */
          dateRangeStore.setDateRange(from, to);
          loadData();
        }
        break;
      }
    }
  }

  function getSearchQueryAgentConversations(data) {
    return encodeURIComponent(`AgentName:"${data.AgentName}"`);
  }

  function getSearchQueryNumberConversations(data) {
    return encodeURIComponent(`Number:"${data.Number}"`);
  }

  function getSearchQueryTagConversations(tag) {
    return encodeURIComponent(`tags:"${tag}"`);
  }

  function onSelectFilter(filter) {
    // TODO pass which filter to make active
    showFilters();
  }

  function showFilters() {
    filtersOpen.value = true;
  }

  function onSetFilter(field, value) {
    filtersStore.setFilterValue(field, value);
  }

  function setFiltersFromQuery() {
    if (!queryFilter || queryFilter === '') {
      return;
    }
    const f = Array.isArray(queryFilter) ? queryFilter : [queryFilter];
    if (f.length <= 0) {
      return;
    }
    const fDefs = f.map((x) => decodeURIComponent(x));
    console.log(`Setting up filters from query:`, fDefs);
    fDefs.map((x) => {
      let xp = x.split(':');
      if (Array.isArray(xp) && xp.length === 2) {
        filtersStore.setFilterValue(xp[0], [xp[1]]);
        return;
      }
      xp = x.split('>');
      if (Array.isArray(xp) && xp.length === 2) {
        filtersStore.setRangeFilterValue(xp[0], 'gte', [xp[1]]);
      }
    });
  }

  function onUploadRecording() {
    dialogUploadRecording.value.start();
  }

  function onImportSelect(importItem) {
    switch (importItem.type) {
      case 'recording':
        dialogUploadRecording.value.start();
        break;
      case 'zendeskTicket':
        dialogUploadZendeskTicket.value.start();
        break;
      case 'freshdeskTicket':
        dialogUploadFreshdeskTicket.value.start();
        break;
      case 'freshserviceTicket':
        dialogUploadFreshserviceTicket.value.start();
        break;
      case 'freshchatConversation':
        dialogUploadFreshchatConversation.value.start();
        break;
      case 'kustomerConversation':
        dialogUploadKustomerConversation.value.start();
        break;
      case 'gorgiasTicket':
        dialogUploadGorgiasTicket.value.start();
        break;
      case 'salesForceCase':
        dialogUploadSalesForceCase.value.start();
        break;
      case 'intercomConversation':
        dialogUploadIntercomConversation.value.start();
        break;
    }
  }

  function onRecordingAnalyzed() {
    loadData();
  }

  function onZendeskTicketAnalyzed() {
    loadData();
  }

  function onFreshchatConversationAnalyzed() {
    loadData();
  }

  function onFreshdeskTicketAnalyzed() {
    loadData();
  }

  function onFreshserviceTicketAnalyzed() {
    loadData();
  }

  function onKustomerConversationAnalyzed() {
    loadData();
  }

  function onGorgiasTicketAnalyzed() {
    loadData();
  }

  function onSalesForceCaseAnalyzed() {
    loadData();
  }

  function onIntercomConversationAnalyzed() {
    loadData();
  }

  // todo check if anything changed, and we need to reload or not
  async function loadDataAssistantData(dataType) {
    const allConversations = await conversationSearchStore.executeExport();

    //const processedConversations = allConversations.map((x) => botConversationHelpers.preProcessBotConversation(x));

    // Convert them to JSON
    //const convMd = botConversationHelpers.botConversations2TranscriptsMd(processedConversations, dataType);
    const convs = conversationHelpers.conversations2AnalysisJSON(allConversations, dataType);

    // Pass to data assistant
    dataAssistantStore.setData({ conversations: convs });
  }

  async function onToggleDA() {
    if (dataAssistantStore.visible) {
      dataAssistantStore.hide();
      return;
    }
    // start dialog
    dialogDADataSelector.value.start(conversationSearchStore.conversationsTotal);
  }

  function getDADataLabel(dataType) {
    let total = conversationSearchStore.conversationsTotal;
    if (total > 500) {
      total = 500;
    }
    let label = `Analyzing ${dataType === 'summary' ? 'Summaries' : 'Transcripts'} of ${total} Conversations over data range ${analyzedDateRange.value} (UTC)`;
    return label;
  }

  function getDADataDescription(dataType) {
    let total = conversationSearchStore.conversationsTotal;
    if (total > 500) {
      total = 500;
    }

    let description =
      `Data contains ${total} customer service conversation records.\n` +
      `Conversations span over data range ${analyzedDateRange.value} (UTC).\n` +
      'Each conversation is represented by one entry in the "conversations" array, and includes the following properties:\n' +
      '- ID: unique conversation ID.\n' +
      '- Date: The date when conversation started.\n' +
      '- CSat: Customer satisfaction rating, on a range from 1 to 10 where 1 is worst and 10 is best.\n' +
      '- AgentName: Name of the Agent who handled conversation if it was escalated.\n' +
      '- Category: Conversation category.\n' +
      '- Subcategory: Conversation subcategory.\n' +
      '- Subsubcategory: Conversation tertiary category.\n';

    if (dataType === 'summary') {
      description += '- Summary: the summary of the conversation.\n';
    } else {
      description += '- Transcript: full transcript of the conversation.\n';
    }

    description += '\n\n';

    return description;
  }

  async function onDADataSelected(selection) {
    const dataType = selection.dataType;
    console.log(`DA - Analyzing`, dataType);
    await loadDataAssistantData(dataType);
    dataAssistantStore.setDataInfo({
      description: getDADataDescription(dataType),
      label: getDADataLabel(dataType),
    });
    dataAssistantStore.show();
  }

  onMounted(() => {
    filtersStore.initFilters('conv', stdConversationFilters);
    setFiltersFromQuery();
    searchProgressButton.value.startProgress();
    loadData();
    setImportItems();

    dataAssistantStore.init({
      assistantDataTitle: dataTitle,
      //assistantDataDescription: dataDescription,
      assistantSuggestedQuestions: suggestedQuestions,
      show: false,
      float: true,
      initialWidth: 1000,
    });
  });
</script>
